import { type FC, useMemo } from "react";
import { Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { type Organism } from "@/graphql.ts";
import { SelMessage } from "@/messages/SelMessage.tsx";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { useFetchGenomeNucleotideStatistics } from "@/ui-modules/entities/genomes/graphql/useFetchGenomeStatistics.ts";

export interface IGenomicNucleotideContentCardProps extends GridCard {
  genomeId: Organism["id"];
}

const GenomicNucleotideContentCard: FC<IGenomicNucleotideContentCardProps> = (props) => {
  const { data, loading, error } = useFetchGenomeNucleotideStatistics({ genomeId: props.genomeId });
  const genomeStatistics = data;

  const markerCounts = useMemo(() => genomeStatistics?.markersCount ?? [], [genomeStatistics?.markersCount]);
  const nuFeatureCounts = useMemo(() => genomeStatistics?.nuFeaturesCount ?? [], [genomeStatistics?.nuFeaturesCount]);

  return (
    <Card key="genome-nu-content-card" title={"Genomic Features"}>
      {loading ? (
        <LoadingSpinner />
      ) : error?.message ? (
        // Fallback. Usually graphql errors are handled globally
        <ContentPlaceholder>{error.message}</ContentPlaceholder>
      ) : markerCounts.length === 0 && nuFeatureCounts.length === 0 ? (
        //  if it is not loading, we either have or don't have data
        <SelMessage id={"noContent.nucleotideContent"} />
      ) : (
        <>
          {markerCounts.map(
            (markersCount) =>
              markersCount && (
                <CardRowSimple
                  label={`${markersCount.markerTypeName} marker`}
                  value={formatNumberByThousands(markersCount.count)}
                  key={"card-row-marker-" + markersCount.markerTypeName}
                />
              ),
          )}
          {nuFeatureCounts.map(
            (nuFeaturesCount) =>
              nuFeaturesCount && (
                <CardRowSimple
                  label={`${nuFeaturesCount.featureTypeName} feature`}
                  value={formatNumberByThousands(nuFeaturesCount.count)}
                  key={"card-row-nu-feature-" + nuFeaturesCount.featureTypeName}
                />
              ),
          )}
        </>
      )}
    </Card>
  );
};

export { GenomicNucleotideContentCard };
